import type { ProjectState } from '@angelmyth/contracts';
import { api, ApiClientError } from './api';

const projectSlug = 'angelmyth';
const storageKey = 'angelmyth-plan-v01';
const legacyStorageKey = 'andrewmatch-plan-v01';
const cacheKey = 'angelmyth-server-cache-v1';

type StatusHandler = (message: string, kind: 'success' | 'warning' | 'error') => void;

class StateClient {
  private revision = 0;
  private timer: number | undefined;
  private saving = false;
  private queued: ProjectState | null = null;
  private profitTimer: number | undefined;
  private profitSaving = false;
  private queuedProfitPrices: number[] | null = null;
  private statusHandler: StatusHandler | null = null;
  private currentUserId = '';
  private currentUserRole = 'admin';

  setStatusHandler(handler: StatusHandler): void { this.statusHandler = handler; }
  setCurrentUser(userId: string, role = 'admin'): void {
    this.currentUserId = userId;
    this.currentUserRole = role;
  }

  async initialize(): Promise<ProjectState> {
    const local = this.readLocal();
    try {
      const remote = await api.getState(projectSlug);
      this.revision = remote.revision;
      if (remote.revision === 0 && Object.keys(remote.data).length === 0 && Object.keys(local).length > 0) {
        this.statusHandler?.('正在迁移本机历史数据与图片…', 'warning');
        const normalizedLocal = await this.migrateEmbeddedImages(structuredClone(local));
        const migrated = await api.updateState(projectSlug, 0, normalizedLocal);
        this.revision = migrated.revision;
        this.cache(migrated.data);
        this.statusHandler?.('本机历史数据已迁移到共享服务器', 'success');
        return migrated.data;
      }
      this.cache(remote.data);
      return remote.data;
    } catch (error) {
      const cached = this.readCache();
      this.statusHandler?.('服务器暂时不可用，当前显示本机缓存；恢复连接后请刷新页面', 'warning');
      console.error('State initialization failed', error);
      return Object.keys(cached).length > 0 ? cached : local;
    }
  }

  scheduleSave(data: ProjectState): void {
    if (!this.canEditSharedState()) {
      this.statusHandler?.('01–11栏为管理员可编辑内容，当前账号为只读', 'warning');
      return;
    }
    this.queued = structuredClone(data);
    if (this.timer) window.clearTimeout(this.timer);
    this.timer = window.setTimeout(() => void this.flush(), 250);
  }

  /** Flush a pending edit immediately (used when an input loses focus or the page is leaving). */
  async flushNow(): Promise<void> {
    if (this.timer) {
      window.clearTimeout(this.timer);
      this.timer = undefined;
    }
    await this.flush();
  }

  scheduleProfitPrices(prices: number[]): void {
    this.queuedProfitPrices = prices.slice(0, 8).map(value => Math.max(0, Number(value) || 0));
    if (this.profitTimer) window.clearTimeout(this.profitTimer);
    this.profitTimer = window.setTimeout(() => void this.flushProfitPrices(), 250);
  }

  async flushProfitPricesNow(): Promise<void> {
    if (this.profitTimer) {
      window.clearTimeout(this.profitTimer);
      this.profitTimer = undefined;
    }
    await this.flushProfitPrices();
  }

  private canEditSharedState(): boolean {
    return this.currentUserRole === 'admin'
      || this.currentUserRole === 'creator'
      || this.currentUserId === 'angelmyth-creator';
  }

  private async flush(): Promise<void> {
    if (this.saving || !this.queued) return;
    this.saving = true;
    const data = this.queued;
    this.queued = null;
    try {
      const saved = await api.updateState(projectSlug, this.revision, data);
      this.revision = saved.revision;
      this.cache(saved.data);
      this.statusHandler?.('已保存到共享服务器', 'success');
    } catch (error) {
      if (error instanceof ApiClientError && error.status === 409) {
        this.queued = null;
        try { localStorage.setItem('angelmyth-conflict-draft-v1', JSON.stringify(data)); } catch { /* best effort */ }
        this.statusHandler?.('其他同事刚刚更新了内容，请刷新页面后重新编辑', 'error');
      } else if (error instanceof ApiClientError && error.status === 401) {
        this.queued = null;
        this.cache(data);
        this.statusHandler?.('登录已过期，正在返回登录页面', 'error');
        window.dispatchEvent(new CustomEvent('angelmyth:auth-expired'));
      } else {
        this.queued = data;
        this.cache(data);
        this.statusHandler?.('服务器保存失败，已保留本机缓存并将在下次修改时重试', 'warning');
      }
      console.error('State save failed', error);
    } finally {
      this.saving = false;
      if (this.queued) this.timer = window.setTimeout(() => void this.flush(), 5000);
    }
  }

  private async flushProfitPrices(): Promise<void> {
    if (this.profitSaving || !this.queuedProfitPrices) return;
    this.profitSaving = true;
    const prices = [...this.queuedProfitPrices];
    this.queuedProfitPrices = null;
    try {
      const saved = await api.updateProfitPrices(projectSlug, prices);
      this.revision = saved.revision;
      this.cache(saved.data);
      this.statusHandler?.('售价已自动保存到共享服务器', 'success');
    } catch (error) {
      this.queuedProfitPrices = prices;
      if (error instanceof ApiClientError && error.status === 401) {
        this.queuedProfitPrices = null;
        this.statusHandler?.('登录已过期，正在返回登录页面', 'error');
        window.dispatchEvent(new CustomEvent('angelmyth:auth-expired'));
      } else {
        this.statusHandler?.('售价自动保存失败，系统将在稍后重试', 'warning');
      }
      console.error('Profit price save failed', error);
    } finally {
      this.profitSaving = false;
      if (this.queuedProfitPrices) this.profitTimer = window.setTimeout(() => void this.flushProfitPrices(), 5000);
    }
  }

  async uploadImage(file: File): Promise<string> {
    try { return await api.uploadImage(file, projectSlug); }
    catch (error) {
      if (!(error instanceof TypeError) && !(error instanceof ApiClientError && error.status >= 500)) throw error;
      this.statusHandler?.('团队服务器暂时不可用，图片已保存在本机草稿中', 'warning');
      return compressImageLocally(file);
    }
  }

  private async migrateEmbeddedImages(data: ProjectState): Promise<ProjectState> {
    const plans = Array.isArray(data.productLaunchPlans) ? data.productLaunchPlans : [];
    for (const plan of plans) {
      if (!isRecord(plan) || !Array.isArray(plan.segments)) continue;
      for (const segment of plan.segments) {
        if (!isRecord(segment) || !Array.isArray(segment.scenes)) continue;
        for (const scene of segment.scenes) {
          if (!isRecord(scene) || !Array.isArray(scene.themes)) continue;
          for (const theme of scene.themes) {
            if (!isRecord(theme)) continue;
            for (const field of ['referenceImages', 'finalImages'] as const) {
              if (!Array.isArray(theme[field])) continue;
              theme[field] = await Promise.all(theme[field].map(async (value, index) => {
                if (typeof value !== 'string' || !value.startsWith('data:image/')) return value;
                const file = dataUrlToFile(value, `legacy-${field}-${index}.webp`);
                return this.uploadImage(file);
              }));
            }
          }
        }
      }
    }
    return data;
  }

  private readLocal(): ProjectState {
    try { return JSON.parse(localStorage.getItem(storageKey) || localStorage.getItem(legacyStorageKey) || '{}') as ProjectState; }
    catch { return {}; }
  }

  private readCache(): ProjectState {
    try { return JSON.parse(localStorage.getItem(cacheKey) || '{}') as ProjectState; }
    catch { return {}; }
  }

  private cache(data: ProjectState): void {
    try { localStorage.setItem(cacheKey, JSON.stringify(data)); }
    catch (error) { console.warn('Unable to cache state locally', error); }
  }
}

export const stateClient = new StateClient();

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function dataUrlToFile(dataUrl: string, filename: string): File {
  const match = /^data:([^;,]+);base64,(.*)$/.exec(dataUrl);
  if (!match?.[1] || !match[2]) throw new Error('历史图片格式无法识别');
  const binary = atob(match[2]);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  return new File([bytes], filename, { type: match[1] });
}

function compressImageLocally(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    if (!file.type.startsWith('image/')) { reject(new Error('请选择图片文件')); return; }
    if (file.size > 8 * 1024 * 1024) { reject(new Error('单张图片不能超过8MB')); return; }
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('图片读取失败'));
    reader.onload = () => {
      const image = new Image();
      image.onerror = () => reject(new Error('图片格式无法识别'));
      image.onload = () => {
        const maxSide = 1200;
        const ratio = Math.min(1, maxSide / Math.max(image.width, image.height));
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(image.width * ratio));
        canvas.height = Math.max(1, Math.round(image.height * ratio));
        const context = canvas.getContext('2d');
        if (!context) { reject(new Error('图片处理失败')); return; }
        context.drawImage(image, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL('image/webp', 0.8));
      };
      image.src = String(reader.result);
    };
    reader.readAsDataURL(file);
  });
}
